import { fmtDatetime } from "utils/time-utils";
import { ZoneId } from "@js-joda/core";
import { ChartData } from "../../dataset-window/models/types";
import { PlotDataEx } from "../interfaces";

type MultiScatterChartConfigProps = {
  xData: ChartData;
  yData: ChartData;
  colorData?: ChartData;
  sizeData?: ChartData;
  colorscale?: boolean;
  selectedColorPalette: string[];
  dataToDisplay: string[] | number[];
  displayTimezone: ZoneId;
};

const createPlotData = ({
  sizeData,
  xData,
  yData,
  colorData,
  selectedColorPalette,
  colorscale,
  dataToDisplay,
  displayTimezone,
}: MultiScatterChartConfigProps): PlotDataEx[] => {
  const dateStrings = xData.keyArr.map(date => {
    return fmtDatetime(date, displayTimezone);
  });

  const hoverTemplateParts = [
    `<b>%{text}</b>`,
    `${xData.title}: <b>%{x:.3f}</b> ${xData.unit}`,
    `${yData.title}: <b>%{y:.3f}</b> ${yData.unit}`,
  ];

  if (colorData) {
    hoverTemplateParts.push(`${colorData.title}: <b>%{marker.color:.3f}</b>, ${colorData.unit}`);
  }
  if (sizeData) {
    hoverTemplateParts.push(`${sizeData.title}: <b>%{customdata:.3f}</b>, ${sizeData.unit}`);
  }

  const hoverTemplate = `${hoverTemplateParts.join("<br>")}<extra></extra>`;

  const getRelativeSizeArr = (array: number[], minSize: number, maxSize: number): number[] => {
    return array.map(item => {
      const currentSize = Math.round(item);
      if (currentSize > maxSize) {
        return maxSize;
      }
      if (currentSize < minSize) {
        return minSize;
      }
      return currentSize;
    });
  };

  function splitArraysByValue({
    x,
    y,
    colors,
    sizes = [],
  }: {
    colors?: Array<number>;
    x?: Array<number>;
    y?: Array<number>;
    sizes?: Array<number>;
  }): Array<{ colors: Array<number>; x: Array<number>; y: Array<number>; size?: Array<number> }> | undefined {
    if (!colors || !x || !y) {
      return undefined;
    }

    if (colors.length !== x.length || colors.length !== y.length || (sizes.length > 0 && colors.length !== sizes.length)) {
      throw new Error("Arrays must have the same length");
    }
    const uniqueValues = new Set(colors);
    const result: Array<{
      colors: Array<number>;
      x: Array<number>;
      y: Array<number>;
      size?: Array<number>;
    }> = [];

    uniqueValues.forEach(value => {
      const subArray1 = [];
      const subArray2 = [];
      const subArray3 = [];
      const subArray4 = [];

      for (let i = 0; i < colors.length; i++) {
        if (colors[i] === value) {
          subArray1.push(colors[i]);
          subArray2.push(x[i]);
          subArray3.push(y[i]);

          if (sizes.length > 0) {
            subArray4.push(sizes[i]);
          }
        }
      }

      result.push({ x: subArray2, y: subArray3, size: subArray4, colors: subArray1 });
    });
    return result;
  }

  if (colorscale) {
    return [
      {
        name: `${colorData?.title}`,
        type: "scatter",
        mode: "markers",
        showlegend: false,
        unit: yData.unit,
        hovertemplate: hoverTemplate,
        x: xData.valueArr,
        y: yData.valueArr,
        opacity: 0.9,
        marker: {
          color: colorData?.valueArr.length ? colorData.valueArr : undefined,
          colorscale: [
            [0.0, "rgb(99,110,250)"],
            [0.1, "rgb(113,108,231)"],
            [0.2, "rgb(127,105,212)"],
            [0.3, "rgb(141,103,193)"],
            [0.4, "rgb(155,100,174)"],
            [0.5, "rgb(169,98,155)"],
            [0.6, "rgb(183,95,135)"],
            [0.7, "rgb(197,93,116)"],
            [0.8, "rgb(211,90,97)"],
            [0.9, "rgb(225,88,78)"],
            [1.0, "rgb(239,85,59)"],
          ],
          size: sizeData?.valueArr.length ? getRelativeSizeArr(sizeData.valueArr, 8, 40) : undefined,
          showscale: !!colorData,
        },
        customdata: sizeData?.valueArr,
        text: dateStrings,
      } as PlotDataEx,
    ];
  }
  if (colorData && [...new Set(colorData.valueArr)].length > 24) {
    console.error("Too many unique color values > 24");
    return [];
  }
  const values = splitArraysByValue({
    colors: colorData?.valueArr,
    sizes: sizeData?.valueArr,
    x: xData.valueArr,
    y: yData.valueArr,
  });
  if (!values) {
    console.error("Different array sizes");
    return [];
  }

  return values.map(({ x, y, colors, size }, index) => {
    return {
      name: `${colors[0].toFixed(colorData?.decimalPlaces || 2)}`,
      type: "scatter",
      mode: "markers",
      showlegend: true,
      unit: yData.unit,
      hovertemplate: hoverTemplate,
      x,
      y,
      opacity: 0.7,
      valueToDisplay: dataToDisplay?.[index]?.value ?? undefined,
      marker: {
        color: colors.map(() => {
          return dataToDisplay && dataToDisplay.length > 0 ? dataToDisplay[index].color : selectedColorPalette[index];
        }),
        autocolorscale: true,
        size: sizeData?.valueArr.length ? getRelativeSizeArr(sizeData.valueArr, 8, 40) : undefined,
      },
      customdata: sizeData?.valueArr,
      text: dateStrings,
    } as PlotDataEx;
  });
};

export const configMultiScatterChartData = (props: MultiScatterChartConfigProps): PlotDataEx[] => {
  return createPlotData(props);
};
